/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import RouteManager from '@ion/applications/src/routes/RouteManager';
import {Page} from '@ion/applications/src/types/pages';
import {info} from '@ion/core/src/logger/logger';
import {removeQueryString} from '@ion/core/src/utils/QueryStringUtils';
import {isNavigationBlocked} from '@/routes/NavigationGuard';
import changePage, {
  getRoute,
  isRoute,
  Route,
  setPersistedRoute
} from '@/routes/RouteDefinitions';
import {NavState} from '@/store/reducers/nav';
import {authorizedPages} from '../../utils/nav-utils';
import {preinitialization} from './initialization';

export const SET_PAGE                  = 'SET_PAGE';
export const SET_PREVIOUS_PAGE         = 'SET_PREVIOUS_PAGE';
export const SET_SIDE_SHEET_STATUS     = 'SET_SIDE_SHEET_STATUS';
export const TOGGLE_SECONDARY_NAV      = 'TOGGLE_SECONDARY_NAV';
export const TOGGLE_USER_PROFILE_VIEW  = 'TOGGLE_USER_PROFILE_VIEW';
export const PAGE_SECURITY_INITIALIZED = 'PAGE_SECURITY_INITIALIZED';
export const SET_ACTIVE_INDEX          = 'SET_ACTIVE_INDEX';
export const SET_NAV_COLLAPSED_STATUS  = 'SET_NAV_COLLAPSED_STATUS';

/**
 * The setPage action is the initial action that is invoked when an iOn page
 * is loaded. The first call will determine if the pre-initialization phase
 * has been completed; if not, the application pre-initialization process
 * is invoked.
 */
export const setPage = (uri, isPermitted = false) => {
  return (dispatch, getState) => {
    if (!getState().initialization.preInitializationComplete) {
      dispatch(preinitialization());
    }
    const {pages}: NavState = getState().nav;
    if (isPermitted || isPermittedPage(pages, uri)) {
      if (!isRoute(Route.LOGIN)) {
        setPersistedRoute(uri);
      }
      dispatch({type: SET_PAGE, page: uri});
    }
  };
};

/**
 * Invoked exclusively via the initializationComplete action creator upon completion
 * of the application initialization phase, at which point a route change listener is
 * added to the Next.js Router which, upon a route change event, verifies that the
 * User has permission to view the given route / page.
 */
export const initializePageSecurity = () => {
  return (dispatch, getState) => {
    if (!getState().nav.pageSecurityInitialized) {
      RouteManager.addEventListener('routeChangeStart', function(url) {
        verifyRouteChange(url, dispatch, getState);
      });
      info('Page Security initialized');
      dispatch({type: PAGE_SECURITY_INITIALIZED});
    }
  };
};

/**
 * @function toggleSecondaryNav
 */
export const toggleSecondaryNav = (show: boolean, index: number) => (
  {type: TOGGLE_SECONDARY_NAV, show, index}
);

// dispatch to set the nav active index...
export const setActiveIndex = (index: number) => ({type: SET_ACTIVE_INDEX, index});

/**
 * @setSideSheetStatus
 */
export const setSideSheetStatus = data => (
  {type: SET_SIDE_SHEET_STATUS, data}
);

/**
 * @function toggleUserProfileView
 */
export const toggleUserProfileView = (view: boolean) => (
  {type: TOGGLE_USER_PROFILE_VIEW, view}
);

/**
 * @verifyRouteChange
 */
export const verifyRouteChange = (url: string, dispatch: Function, getState: Function) => {
  const {pages, currentPage}: NavState = getState().nav;
  if (!isNavigationBlocked(url, currentPage)) {
    const route = getRoute(url as unknown as Route);
    if (route !== currentPage) {
      if (isPermittedPage(pages, route)) {
        dispatch(setPage(route, true));
      } else {
        currentPage ? changePage(currentPage as Route) : RouteManager.back();
        throw `[CTC Admin Security Violation]: Route ${url} is not permitted. This error can safely be ignored`;
      }
    }
  } else {
    throw `[CTC Admin Navigation Guard]: Route ${url} is blocked. This error can safely be ignored`;
  }
};

/**
 * The `isPermittedPage` helper function determines if the user has access to the given
 * page. This function determines access based on the `getRoutes` function, which in
 * turn determines the pages the user can access based on the Features assigned to
 * their Role. In addition, if the given `uri` contains a query string, the path
 * will be extracted and used to determine if the user has access to the provided
 * Route.
 *
 * @returns `true` if the user has access to the provided `uri` (route), otherwise,
 *          returns `false`
 */
const isPermittedPage = (pages: Page[] = [], uri: string): boolean => (
  getRoutes(pages).includes(removeQueryString(uri))
);

/**
 * @getRoutes
 *
 * Collects and returns a list of all possible routes for the given pages.
 *
 * @param pages list of application pages
 * @returns flattened map of all parent and child page routes
 */
const getRoutes = (pages): string[] => {
  const authorized = authorizedPages(pages);
  return authorized.map(({route, pages: childPages}) => (
    childPages ? getRoutes(childPages) : getRoute(route)
  )).flat();
};

/**
 * Provides a utility function to store previous page.
 */
export const setPreviousPage = route => (
  {type: SET_PREVIOUS_PAGE, route}
);

export const setNavCollapsedStatus = collapsed => (
  {type: SET_NAV_COLLAPSED_STATUS, collapsed}
);
